import AsyncStorage from '@react-native-async-storage/async-storage';
import { useNavigation } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';

import { useSubscription } from '../contexts/SubscriptionContext';
import { ms, s, vs } from '../utils/responsive';
import { TAP } from '../utils/pointerWords';



interface TrialBannerProps {
  compact?: boolean;
}

const TrialBanner: React.FC<TrialBannerProps> = ({ compact = false }) => {
  const navigation = useNavigation<any>();
  const { subscription, trialDaysRemaining, hasStartedOwnTrial } = useSubscription();
  const [isLetterFirstUser, setIsLetterFirstUser] = useState(false);

  useEffect(() => {
    AsyncStorage.getItem('user')
      .then((raw) => {
        if (!raw) return;
        const user = JSON.parse(raw);
        setIsLetterFirstUser((user?.onboardingVersion ?? 0) >= 2);
      })
      .catch(() => {});
  }, []);

  // Don't show banner if no subscription info
  if (!subscription) return null;

  const status = subscription.subscriptionStatus;
  const tier = subscription.subscriptionTier;

  // Don't show for users who haven't started a trial yet
  // (UpgradeBanner handles the "Start Free Trial" case)
  if (!hasStartedOwnTrial) {
    return null;
  }

  // Don't show for paid users (non-trial tiers with active status).
  // The letter tier is the exception: it sells delivery only, so a
  // concurrently running test drive still deserves its countdown.
  if (tier !== 'trial' && tier !== 'expired' && tier !== 'letter' && status === 'active') {
    return null;
  }
  if (tier === 'letter' && !(trialDaysRemaining !== null && trialDaysRemaining > 0)) {
    return null;
  }

  const handlePress = () => {
    // Letter-first users (onboardingVersion 2) upgrade through the tier
    // ladder; everyone else keeps the existing subscription screen.
    if (isLetterFirstUser) {
      navigation.navigate('LetterFirst', { screen: 'LetterLevels' });
    } else {
      navigation.navigate('Profile', { screen: 'SubscriptionManagement' });
    }
  };

  // Determine banner style and message based on status
  const getBannerConfig = () => {
    if (status === 'expired') {
      return {
        backgroundColor: '#FEE2E2',
        borderColor: '#FECACA',
        textColor: '#991B1B',
        icon: '⚠️',
        message: 'Trial expired - Upgrade to edit data',
        actionText: 'Upgrade Now',
      };
    }

    if (status === 'grace_period') {
      return {
        backgroundColor: '#FEF3C7',
        borderColor: '#FDE68A',
        textColor: '#92400E',
        icon: '⏳',
        message: 'Grace period - Upgrade soon to keep editing',
        actionText: 'Upgrade',
      };
    }

    // Active trial with time left — persistent one-line reminder from
    // day one, per owner feedback (previously hidden until <= 7 days).
    if (
      (tier === 'trial' || tier === 'letter') &&
      status === 'active' &&
      trialDaysRemaining !== null &&
      trialDaysRemaining > 7
    ) {
      return {
        backgroundColor: '#FEF3C7',
        borderColor: '#FDE68A',
        textColor: '#92400E',
        icon: '\u23F1\uFE0F',
        message: `On your 14-day test drive \u2014 ${trialDaysRemaining} days left. ${TAP} to upgrade.`,
        actionText: 'Upgrade',
      };
    }

    // Active but low days remaining (warning)
    if (trialDaysRemaining !== null && trialDaysRemaining <= 7) {
      return {
        backgroundColor: '#FEF3C7',
        borderColor: '#FDE68A',
        textColor: '#92400E',
        icon: '⏰',
        message: `Trial ends in ${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'}`,
        actionText: 'Upgrade',
      };
    }

    // Beta/discount users - show info banner
    if (subscription.discountTier && subscription.discountTier.startsWith('beta')) {
      return {
        backgroundColor: '#DBEAFE',
        borderColor: '#BFDBFE',
        textColor: '#1E40AF',
        icon: '🎉',
        message: `Beta access: ${trialDaysRemaining ?? 0} days remaining`,
        actionText: null,
      };
    }

    return null;
  };

  const config = getBannerConfig();
  if (!config) return null;

  if (compact) {
    return (
      <TouchableOpacity
        style={[
          styles.compactBanner,
          { backgroundColor: config.backgroundColor, borderColor: config.borderColor },
        ]}
        onPress={handlePress}
        disabled={!config.actionText}
        accessibilityRole={config.actionText ? "button" : "text"}
        accessibilityLabel={config.message}
        accessibilityHint={config.actionText ? "Double tap to upgrade your subscription" : undefined}
      >
        <Text style={[styles.compactText, { color: config.textColor }]} accessibilityElementsHidden={true}>
          {config.icon} {config.message}
        </Text>
      </TouchableOpacity>
    );
  }

  return (
    <View
      style={[
        styles.banner,
        { backgroundColor: config.backgroundColor, borderColor: config.borderColor },
      ]}
      accessibilityRole="alert"
      accessibilityLabel={config.message}
    >
      <View style={styles.bannerContent}>
        <Text style={styles.bannerIcon} accessibilityElementsHidden={true}>{config.icon}</Text>
        <Text style={[styles.bannerText, { color: config.textColor }]}>
          {config.message}
        </Text>
      </View>
      {config.actionText && (
        <TouchableOpacity
          style={styles.actionButton}
          onPress={handlePress}
          accessibilityRole="button"
          accessibilityLabel={config.actionText}
          accessibilityHint="Double tap to upgrade your subscription"
        >
          <Text style={styles.actionButtonText}>{config.actionText}</Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: ms(10),
    paddingHorizontal: ms(15),
    borderBottomWidth: 1,
  },
  bannerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  bannerIcon: {
    fontSize: ms(16, 0.3),
    marginRight: ms(8),
  },
  bannerText: {
    fontSize: ms(13, 0.3),
    fontWeight: '500',
    flex: 1,
  },
  actionButton: {
    backgroundColor: '#1C2C4F',
    paddingVertical: ms(6),
    paddingHorizontal: ms(12),
    borderRadius: ms(15),
    marginLeft: ms(10),
  },
  actionButtonText: {
    color: '#FFF',
    fontSize: ms(12, 0.3),
    fontWeight: '600',
  },
  compactBanner: {
    paddingVertical: ms(6),
    paddingHorizontal: ms(12),
    borderRadius: ms(8),
    borderWidth: 1,
  },
  compactText: {
    fontSize: ms(12, 0.3),
    fontWeight: '500',
    textAlign: 'center',
  },
});

export default TrialBanner;
